import { useEffect, useState } from 'react';

/**
 * Progressive copy for react-query loads so long waits don't look frozen.
 * - Initial (no data yet): "Loading…" → "Still loading… Ns" after 3s
 * - Background refetch (data present): "Updating…" → "Still updating… Ns"
 */
export function useQueryLoadStatus({
  isLoading = false,
  isFetching = false,
  hasData = false,
  initialLabel = 'Loading…',
  updatingLabel = 'Updating…',
  slowAfterSec = 3,
} = {}) {
  const busy = Boolean(isLoading || isFetching);
  const [elapsedSec, setElapsedSec] = useState(0);

  useEffect(() => {
    if (!busy) {
      setElapsedSec(0);
      return undefined;
    }
    const started = Date.now();
    setElapsedSec(0);
    const id = window.setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - started) / 1000));
    }, 1000);
    return () => window.clearInterval(id);
  }, [busy]);

  if (!busy) {
    return {
      message: null,
      isInitial: false,
      isBackground: false,
      elapsedSec: 0,
    };
  }

  if (!hasData) {
    const message =
      elapsedSec >= slowAfterSec
        ? `Still loading… ${elapsedSec}s`
        : initialLabel;
    return {
      message,
      isInitial: true,
      isBackground: false,
      elapsedSec,
    };
  }

  const message =
    elapsedSec >= slowAfterSec
      ? `Still updating… ${elapsedSec}s`
      : updatingLabel;
  return {
    message,
    isInitial: false,
    isBackground: true,
    elapsedSec,
  };
}
